<template>
  <div class="py-4 container-fluid">
    <!-- Print Button -->
    <div class="row mb-2 no-print">
      <div class="col">
        <button @click="printReport" class="btn btn-primary mb-3">
          <i class="fas fa-print"></i> Print Report
        </button>
      </div>
    </div>

    <!-- Printable Area Start -->
    <div class="printable-area">
      <div class="row mb-4">
        <div class="col">
          <h4 class="mb-2">Stocktake Session Report</h4>
          <div class="card p-3">
            <div class="d-flex flex-wrap">
              <table>
                <tr>
                  <td>
                    <div class="mr-4">
                      <strong>Session:</strong> {{ session_name }}
                    </div>
                  </td>
                  <td>
                    <div class="mr-4">
                      <strong>Store:</strong> {{ store_name }}
                    </div>
                  </td>
                  <td>
                    <div class="mr-4">
                      <strong>Status:</strong>
                      <span
                        :class="
                          session.status === 'Active'
                            ? 'badge badge-success'
                            : 'badge badge-danger'
                        "
                      >
                        {{ status }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>Started:</strong> {{ form_date(start_datetime) }}
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="data-table-container mt-4">
        <table class="data-table">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>System Quantity</th>
              <th>Shelf Quantity</th>
              <th>Variance</th>
              <th>Time Snapshot</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in stockTakeData" :key="entry.id">
              <td>
                <ItemName
                  :ItemName="entry.item_name"
                  :ItemBrand="entry.brand_name"
                  :ItemStrength="entry.item_strength"
                />
              </td>
              <td>{{ entry.quantity_expected }}</td>
              <td>{{ entry.quantity_counted }}</td>
              <td>{{ entry.variance }}</td>
              <td>{{ form_date(entry.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Printable Area End -->
  </div>
</template>

<script>
import { generalApiRequest } from "../../api/apiIndex";
import ItemName from "./components/ItemName.vue";
import formatDateTime from "../../timezone/utc_local_time";
import showNotification from "../../notification/notify";

export default {
  name: "StocktakeReport",
  data() {
    return {
      session: {},
      itemName: "",
      itemId: null,
      systemQty: "",
      shelfQty: "",
      variance: "",
      varianceValue: "",
      sellingPrice: "",
      store_id: this.$route.params.storeLocation,
      session_id: this.$route.params.sessionId,
      session_name: "",
      store_name: "",
      stockTakeData: [],
      start_datetime: "",
      status: "",
    };
  },
  components: {
    ItemName,
  },
  methods: {
    form_date(tm) {
      return formatDateTime(tm);
    },
    async fetchSessionDetails() {
      const res = await generalApiRequest(
        `stocktakes/${this.$route.params.sessionId}/items`,
        "GET",
        {},
        1
      );

      if (res.status === "success") {
        this.stockTakeData = res.data;
        this.session_name = res.session_details.session_name;
        this.store_name = res.session_details.store_name;
        this.start_datetime = res.session_details.start_datetime;
        this.status = res.session_details.status;
      }
    },
    printReport() {
      // Create print styles
      const printStyles = `
        @media print {
          /* Hide everything except printable area */
          body * {
            visibility: hidden;
          }
          
          .printable-area, .printable-area * {
            visibility: visible;
          }
          
          .printable-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
          }
          
          /* Ensure cards and tables are fully visible */
          .card, .data-table {
            border: 1px solid #000 !important;
            box-shadow: none !important;
          }
          
          .data-table th {
            background-color: #f2f2f2 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Page setup */
          @page {
            margin: 1cm;
          }
          
          /* Ensure proper spacing */
          .mb-4 {
            margin-bottom: 1rem !important;
          }
          
          .mt-4 {
            margin-top: 1rem !important;
          }
        }
      `;

      // Add print styles
      const styleSheet = document.createElement("style");
      styleSheet.type = "text/css";
      styleSheet.innerText = printStyles;
      document.head.appendChild(styleSheet);

      // Trigger print
      window.print();

      // Clean up
      setTimeout(() => {
        if (styleSheet.parentNode) {
          document.head.removeChild(styleSheet);
        }
      }, 1000);
    },
    setItem(item) {
      this.itemId = item.id;
      this.systemQty = item.c_quantity;
    },
    calculateVariance() {
      if (this.shelfQty === "" || this.systemQty === "") return;
      this.variance = this.shelfQty - this.systemQty;
      this.varianceValue = this.variance * this.sellingPrice;
    },
    async submitStocktakeItem() {
      if (!this.itemId || this.shelfQty === "") {
        alert("Fill all required fields.");
        return;
      }

      const payload = {
        session_id: this.session_id,
        product_id: this.itemId,
        quantity_expected: this.systemQty,
        quantity_counted: this.shelfQty,
        variance: this.variance,
        variance_equivalent: this.varianceValue,
        remarks: null,
        created_by: 1,
      };

      const res = await generalApiRequest(
        "stocktakes/add-item",
        "POST",
        payload,
        1
      );

      if (res.status === "success") {
        this.itemName = "";
        this.itemId = null;
        this.systemQty = "";
        this.shelfQty = "";
        this.variance = "";
        this.varianceValue = "";
        this.sellingPrice = "";

        showNotification("Successfully added!");
        this.fetchSessionDetails();
      }
    },
    capitalize(str) {
      if (!str) return "";
      return str
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  mounted() {
    this.fetchSessionDetails();
  },
};
</script>

<style scoped>
.badge {
  padding: 0.4em 0.7em;
  border-radius: 20px;
}
.badge-success {
  background: #28a745;
  color: #fff;
}
.badge-danger {
  background: #dc3545;
  color: #fff;
}

.data-table-container {
  width: 100%;
  overflow-x: auto;
  font-family: sans-serif;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
  background-color: white;
  color: black;
}

.data-table th,
.data-table td {
  padding: 5px;
  border: 1px solid #ddd;
  text-align: left;
}

.data-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

/* Print button styling */
.no-print {
  text-align: right;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  padding: 8px 16px;
  font-size: 14px;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}

.fa-print {
  margin-right: 5px;
}

/* Screen-only styles */
@media screen {
  .printable-area {
    position: static;
  }
}
</style>
